import { KeywordForm } from '@indipia/ui-core';
import { ThumbImg } from './ThumbImg';

export interface PhotoCardProps {
  photoId: number;
  thumbUrl: string | null;
  takenAt: string | null;
  /** 촬영일 직접 표시용 문자열. 미제공 시 takenAt을 MM.DD로 자동 포맷 */
  date?: string;
  /** 등록일 ISO 문자열. 미제공 시 - 표시 */
  registeredAt?: string | null;
  tags?: string[];
  /** tags 중 이번 세션에 새로 추가된 값 목록 — 제공 시 기존 태그와 다른 스타일로 구분 표시 */
  newTags?: string[];
  checked?: boolean;

  /** 체크박스 표시 여부 (default: true) */
  showCheckbox?: boolean;
  /** 태그 영역 표시 여부 (default: true) */
  showTags?: boolean;
  /** 날짜 영역 우측에 "사진 상세 >" 안내 라벨 표시 여부 — onToggle이 사진 상세로 이동하는 화면에서만 true (default: false) */
  showDetailHint?: boolean;
  /** 태그 영역 마우스 커서를 기본(default)으로 표시할지 여부. false면 카드 전체의 pointer 커서를 그대로 물려받음 (default: false) */
  tagAreaDefaultCursor?: boolean;
  /** 썸네일 커서를 zoom-in 대신 카드 전체의 pointer 커서로 표시할지 여부 — onViewPhoto가 뷰어가 아니라 상세 이동인 화면에서 true (default: false) */
  thumbAreaDefaultCursor?: boolean;

  onToggle?: () => void;
  /** 체크박스 클릭 전용 콜백. 제공 시 onToggle 대신 사용 */
  onCheckboxToggle?: () => void;
  onViewPhoto?: () => void;
  onAddTag?: (tag: string) => void;
  onRemoveTag?: (tag: string) => void;
  /** 제공 시 카드 우측 상단에 제외/복원 버튼 표시 */
  onRemove?: () => void;
  /** 공유 대상에서 제외된 상태인지 여부 — true면 카드를 흐리게 표시하고 버튼을 복원용으로 전환 (default: false) */
  removed?: boolean;
  /** 제외 동작 비활성화 여부 — 마지막 남은 1장처럼 더 이상 제외할 수 없을 때 (removed=false일 때만 의미 있음, default: false) */
  removeDisabled?: boolean;
}

function fmtDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  return `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

export function PhotoCard({
  thumbUrl,
  takenAt,
  date,
  registeredAt,
  tags = [],
  newTags,
  checked = false,
  showCheckbox = true,
  showTags = true,
  showDetailHint = false,
  tagAreaDefaultCursor = false,
  thumbAreaDefaultCursor = false,
  onToggle,
  onCheckboxToggle,
  onViewPhoto,
  onAddTag,
  onRemoveTag,
  onRemove,
  removed = false,
  removeDisabled = false,
}: PhotoCardProps) {
  return (
    <div
      style={{
        position: 'relative',
        background: 'var(--shell-surface)',
        cursor: onToggle ? 'pointer' : 'default',
        border: `1.5px solid ${checked ? 'var(--gt-primary)' : 'var(--shell-border)'}`,
        borderRadius: 'var(--radius-md)',
        overflow: 'visible',
        transition: 'border-color .12s, opacity .12s',
        boxShadow: checked ? '0 0 0 2px var(--gt-subtle)' : 'none',
        opacity: removed ? 0.45 : 1,
      }}
      onClick={onToggle}
    >
      {/* 공유 제외됨 배지 */}
      {removed && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 1, borderRadius: 'var(--radius-md)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'var(--alpha-black-200)', pointerEvents: 'none',
        }}>
          <span style={{
            fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--gray-0)',
            background: 'var(--alpha-black-700)', borderRadius: 'var(--radius-full)', padding: '3px 10px',
          }}>공유 제외됨</span>
        </div>
      )}

      {/* 체크박스 */}
      {showCheckbox && (
        <button
          type="button"
          aria-label="사진 선택"
          aria-pressed={checked}
          onClick={e => { e.stopPropagation(); (onCheckboxToggle ?? onToggle)?.(); }}
          style={{
            position: 'absolute', top: 2, left: 2, zIndex: 2,
            width: 28, height: 28,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 20, height: 20, borderRadius: 'var(--radius-sm)',
              border: `2px solid ${checked ? 'var(--gt-primary)' : 'var(--alpha-white-900)'}`,
              background: checked ? 'var(--gt-primary)' : 'var(--alpha-black-200)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all .12s',
            }}
          >
            {checked && (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--gray-0)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </span>
        </button>
      )}

      {/* 단건 제외/복원 */}
      {onRemove && (
        <button
          type="button"
          aria-label={removed ? '사진 복원' : '사진 제외'}
          aria-disabled={!removed && removeDisabled}
          onClick={e => { e.stopPropagation(); onRemove(); }}
          style={{
            position: 'absolute', top: 6, right: 6, zIndex: 2,
            width: 18, height: 18, borderRadius: 'var(--radius-full)',
            border: 'none',
            background: !removed && removeDisabled ? 'var(--alpha-black-200)' : 'var(--alpha-black-500)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: !removed && removeDisabled ? 'not-allowed' : 'pointer', padding: 0,
          }}
        >
          {removed ? (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--gray-0)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
            </svg>
          ) : (
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="var(--gray-0)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          )}
        </button>
      )}

      {/* 썸네일 */}
      <button
        type="button"
        onClick={e => { e.stopPropagation(); onViewPhoto?.(); }}
        aria-label={thumbAreaDefaultCursor ? '사진 상세' : '사진 확대'}
        style={{
          aspectRatio: '1 / 1',
          borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
          overflow: 'hidden',
          background: 'var(--gt-subtle)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: !onViewPhoto ? 'default' : (thumbAreaDefaultCursor ? 'pointer' : 'zoom-in'),
          width: '100%', padding: 0, border: 'none',
        }}
      >
        <ThumbImg
          url={thumbUrl}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          fallback={
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
          }
        />
      </button>

      {/* 날짜 — 클릭 시 사진 상세로 이동하는 영역이라 우측에 안내 라벨 표시 */}
      <div style={{ padding: '9px 8px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, borderBottom: showTags ? '1px solid var(--shell-border)' : 'none' }}>
        <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
          촬영일 : {date ?? fmtDate(takenAt)} / 등록일 : {fmtDate(registeredAt)}
        </span>
        {showDetailHint && (
          <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)', whiteSpace: 'nowrap', flexShrink: 0 }}>
            사진 상세 &gt;
          </span>
        )}
      </div>

      {/* 태그 */}
      {showTags && (
        // 태그 편집 영역 클릭이 카드 선택(onToggle)으로 번지지 않게 막는다
        <div onClick={e => { if (tagAreaDefaultCursor || onAddTag || onRemoveTag) e.stopPropagation(); }} style={{ padding: 'var(--spacing-02) var(--spacing-03) var(--spacing-03)', minHeight: 'var(--num-36)', cursor: tagAreaDefaultCursor ? 'default' : undefined }}>
          {onAddTag || tags.length > 0 ? (
            <KeywordForm
              mode={onAddTag ? 'edit' : 'readonly'}
              tags={tags}
              onAdd={onAddTag}
              onRemove={onRemoveTag ? (i) => onRemoveTag(tags[i]) : undefined}
              maxCount={10}
              maxLength={10}
              placeholder="태그 입력 후 엔터"
              highlight={newTags ? (tag) => newTags.includes(tag) : undefined}
            />
          ) : (
            <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)', fontWeight: 500 }}>태그없음</span>
          )}
        </div>
      )}
    </div>
  );
}
